import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import { getOrderById } from '../services/api';
import FroggySpinner from '../components/common/FroggySpinner';
import '../styles/orders.css';

const OrderScheduledPage = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated } = useContext(AuthContext);
  const [loading, setLoading] = useState(true);
  const [order, setOrder] = useState(null);
  const [error, setError] = useState(null);
  const [showToast, setShowToast] = useState(true);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  // Get delivery_date and toast message from navigation state if available
  const deliveryDateFromState = location.state?.delivery_date;
  const toastMessage = location.state?.toast_message;

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }

    // 2026-07-18 refactor: was raw fetch() + localStorage.getItem('authToken');
    // now uses the services/api abstraction which handles auth headers +
    // consistent error paths. Removes the direct-localStorage coupling.
    const fetchOrder = async () => {
      try {
        const data = await getOrderById(orderId);
        // Backend tolerates either wrapped or unwrapped shape.
        const orderFromApi = data?.order ?? data;
        setOrder(orderFromApi);
      } catch (err) {
        console.warn('[OrderScheduledPage] Failed to load order:', err?.message || err);
        setError('Failed to load order details');
      } finally {
        setLoading(false);
      }
    };

    fetchOrder();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, orderId, navigate]);

  // Auto-dismiss toast after 5 seconds
  useEffect(() => {
    if (toastMessage && showToast) {
      const timer = setTimeout(() => {
        setShowToast(false);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [toastMessage, showToast]);

  // Handle window resize for responsive behavior
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const formatDeliveryDate = (dateStr) => {
    if (!dateStr) return '';

    try {
      // Parse the date string and format it for display
      const date = new Date(dateStr + 'T00:00:00');
      return date.toLocaleDateString('en-US', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch (err) {
      return dateStr;
    }
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: 'var(--ff-space-5xl) var(--ff-space-lg)' }}>
        <FroggySpinner size="lg" label="Loading your order" />
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ maxWidth: 720, margin: '0 auto', padding: 'var(--ff-space-3xl) var(--ff-space-lg)' }}>
        <div className="bg-white rounded-lg shadow p-6" style={{ padding: 24, borderRadius: 8, boxShadow: '0 1px 3px rgba(0,0,0,0.1)', background: '#fff' }}>
          <div style={{ color: 'var(--ff-semantic-danger, #dc2626)', marginBottom: 16 }}>{error}</div>
          <button
            type="button"
            onClick={() => navigate('/account/orders')}
            className="ff-btn ff-btn--primary"
          >
            Back to Orders
          </button>
        </div>
      </div>
    );
  }

  // Use delivery_date from state if available, otherwise from order
  const deliveryDate = deliveryDateFromState || order?.delivery_date;

  // Hub detection — same heuristic as CustomerSchedule.js. Server sends
  // delivery_type in a few different shapes across the codebase; check
  // each one plus the presence of hub-specific fields.
  const rawType = (order?.delivery_type || '').toUpperCase();
  const isHub =
    rawType === 'HUB' ||
    order?.delivery_type === 'hub_pickup' ||
    !!order?.hub_address ||
    !!order?.hub_selection ||
    /hub/i.test(order?.shipping_method || '') ||
    /hub/i.test(order?.delivery_type_label || '');

  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: '#f8f9fa',
      paddingTop: isMobile ? '24px' : '48px',
      paddingBottom: '24px',
      paddingLeft: '16px',
      paddingRight: '16px'
    }}>
      {/* Toast notification */}
      {showToast && toastMessage && (
        <div style={{
          position: 'fixed', top: 16, right: 16, zIndex: 50, maxWidth: 360,
        }}>
          <div style={{
            background: 'var(--ff-semantic-success, #16a34a)',
            color: '#fff',
            padding: '10px 20px',
            borderRadius: 8,
            boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 12,
          }}>
            <span>{toastMessage}</span>
            <button
              onClick={() => setShowToast(false)}
              style={{ background: 'transparent', border: 'none', color: '#fff', fontSize: 20, cursor: 'pointer', padding: 0, lineHeight: 1 }}
              aria-label="Dismiss notification"
            >
              ×
            </button>
          </div>
        </div>
      )}

      <div style={{ maxWidth: '720px', margin: '0 auto' }}>
        <div style={{ background: '#fff', borderRadius: 12, boxShadow: '0 1px 3px rgba(0,0,0,0.05)', border: '1px solid #e5e7eb', padding: '32px 24px' }}>
          <div style={{ textAlign: 'center' }}>
            {/* Success Icon */}
            <div style={{ marginBottom: 16 }}>
              <svg
                style={{
                  width: isMobile ? '64px' : '96px',
                  height: isMobile ? '64px' : '96px',
                  margin: '0 auto',
                  color: 'var(--ff-semantic-success, #16a34a)',
                }}
                fill="currentColor"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
              </svg>
            </div>

            {/* Title */}
            <h1 style={{
              fontSize: isMobile ? '24px' : '32px',
              fontWeight: '500',
              color: '#1f2937',
              marginBottom: '8px'
            }}>
              Delivery Scheduled
            </h1>

            {/* Subhead */}
            {deliveryDate && (
              <p style={{
                fontSize: '18px',
                color: '#6b7280',
                fontWeight: '400',
                marginTop: '12px',
                marginBottom: '16px'
              }}>
                We've reserved <strong>{formatDeliveryDate(deliveryDate)}</strong> for your arrival.
              </p>
            )}

            {/* Delivery Method Display */}
            {order && (
              <div style={{
                backgroundColor: '#f0f9ff',
                border: '1px solid #0ea5e9',
                borderRadius: '8px',
                padding: '16px',
                marginBottom: '32px',
                textAlign: 'center'
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  marginBottom: '8px'
                }}>
                  <span style={{ fontSize: '20px' }}>
                    {isHub ? '🏢' : '🏠'}
                  </span>
                  <h3 style={{
                    fontSize: '16px',
                    fontWeight: '600',
                    color: '#0369a1',
                    margin: '0'
                  }}>
                    {isHub ? 'Hub Pickup' : 'Home Delivery'}
                  </h3>
                </div>

                {!isHub ? (
                  <p style={{
                    margin: '0',
                    fontSize: '14px',
                    color: '#0369a1'
                  }}>
                    Your order will be delivered directly to your address
                  </p>
                ) : order.hub_selection ? (
                  <div style={{
                    fontSize: '14px',
                    color: '#0369a1'
                  }}>
                    <p style={{ margin: '0 0 4px 0' }}>
                      <strong>Pickup Location:</strong> {order.hub_selection.name}
                    </p>
                    <p style={{ margin: '0', fontSize: '13px' }}>
                      {order.hub_selection.address?.streetLines?.join(', ')}, {order.hub_selection.address?.city}, {order.hub_selection.address?.stateOrProvinceCode} {order.hub_selection.address?.postalCode}
                    </p>
                  </div>
                ) : (
                  <p style={{
                    margin: '0',
                    fontSize: '14px',
                    color: '#0369a1'
                  }}>
                    FedEx hub pickup (details will be provided with tracking)
                  </p>
                )}
              </div>
            )}
          </div>

          {/* What happens next section */}
          <div className="order-next-steps">
            <h3 className="order-next-steps-title">
              What happens next:
            </h3>
            <ul className="order-next-steps-list">
              <li className="order-next-steps-item step-shipping">
                You'll receive tracking information once your order ships.
              </li>
              <li className="order-next-steps-item step-confirmation">
                We'll email you if weather causes any delays.
              </li>
              <li className="order-next-steps-item step-shipping">
                Your animals will be shipped via Priority Overnight for next-day delivery.
              </li>
            </ul>
          </div>

          {/* CTA Button */}
          {/* R5 B5: the page's one primary, on the ff-btn shape (36px,
              14px, radius 8) — it was a 16px / 12px-padded btn-blue. */}
          <button
            type="button"
            onClick={() => navigate(`/account/orders/${orderId}`)}
            className={`ff-btn ff-btn--primary${isMobile ? ' ff-btn--block' : ''}`}
            style={isMobile ? undefined : { display: 'flex', width: 'fit-content', minWidth: '200px', margin: '0 auto' }}
          >
            View Order Details
          </button>
        </div>
      </div>
    </div>
  );
};

export default OrderScheduledPage;
